import { ImageElementController, ImageElement, TooltipEvent } from '@sas-dvr/ltjs-bird';
import { VisualElementControllerLoader } from './LoaderFactory';
import { loadVisualElementController } from './loadVisualElement';
import { ImageState } from '@sas-dvr/internal-va-react-core/store/birdElement/imageState';
import { ImageMode } from '@sas/nova-commons/components/Image';
import { ImageScaleType, ltjsEnvironment } from '@sas-dvr/ltjs-commons';
import { merge } from 'rxjs';
import { map, distinctUntilChanged, tap, ignoreElements } from 'rxjs/operators';
import { createBirdActionsHandler } from '../util/birdActions';
import { ofActionCreatorType } from '../util/rxUtils';

export const loadImageElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(visualElementController instanceof ImageElementController)) {
    throw new Error('Expected the controller to be a ImageElementController');
  }

  let currentTipEvent: TooltipEvent | undefined;
  const tipHandle = visualElementController.attachEvent(TooltipEvent.SHOW, (e) => {
    if (e instanceof TooltipEvent) {
      if (currentTipEvent) {
        currentTipEvent.release();
      }
      currentTipEvent = e.retain();
    }
  });

  const model = visualElementController.getBIRDModel();
  if (!model || !(model instanceof ImageElement)) {
    throw new Error('Expected the controller to be a ImageElementController');
  }
  model.retain();
  let mode: ImageMode | undefined;
  switch (model.getScaleType()) {
    case ImageScaleType.SCALE:
      mode = 'scale';
      break;
    case ImageScaleType.SCALEHEIGHT:
      mode = 'fit-width';
      break;
    case ImageScaleType.SCALEWIDTH:
      mode = 'fit-height';
      break;
    case ImageScaleType.SCALETOFIT:
      mode = 'fit';
      break;
    case ImageScaleType.TILE:
      mode = 'tile';
      break;
  }
  visualElementController.willAttemptDownload();
  const remoteUrl = visualElementController.getImageRemoteURL();
  let url = '';
  let isExternal = false;
  if (remoteUrl) {
    url = remoteUrl.getFinishedURLString() ?? '';
    isExternal = true;
  } else {
    // accessing the virtual file system is dangerous. Try catch to protect from file not found.
    const fileSystemPath = visualElementController.getImageLocalFilePath();
    if (fileSystemPath) {
      if (state$.value.transport.serverType === 'standalone') {
        // Standalone only has external images.
        isExternal = true;
        url = `${state$.value.transport.packageUrl}image/${fileSystemPath.substr(
          fileSystemPath.lastIndexOf('/') + 1
        )}`;
      } else {
        try {
          url = ltjsEnvironment._ltjsModule.getStringContentsOfFile(fileSystemPath) ?? '';
        } catch (error) {
          visualElementController.didAttemptDownload(false);
          visualElementController.updateWarningStatus();
        }
      }
    } else {
      visualElementController.didAttemptDownload(false);
      visualElementController.updateWarningStatus();
    }
  }

  const visualId = deps.getKey(visualElementController);
  const loadState$ = state$.pipe(
    map((aState) => {
      const imageState = aState.birdElements.Image[visualId];
      if (!imageState) {
        return undefined;
      }
      return imageState.loadState;
    }),
    distinctUntilChanged(),
    tap((loadState) => {
      if (loadState === undefined || loadState === 'loading') {
        return;
      }
      if (loadState === 'error') {
        visualElementController.didAttemptDownload(false);
      } else if (loadState === 'success') {
        visualElementController.didAttemptDownload(true);
        // without this images get stuck in a busy state when switching to a new section then back.
        visualElementController.redispatchReadyEvent();
      }
      visualElementController.updateWarningStatus();
    }),
    ignoreElements()
  );

  const { birdActions$, birdActionsHandler } = createBirdActionsHandler(
    visualId,
    myActions$,
    state$
  );

  const clickAction$ = myActions$.pipe(
    ofActionCreatorType<typeof ImageState.actions.notifyClick>(ImageState.actionTypes.notifyClick),
    tap((action) => {
      const { x, y } = action.payload.data;
      visualElementController.showTooltip(model.getAlternateText(), x, y);
      if (!currentTipEvent) {
        return;
      }
      birdActionsHandler(currentTipEvent, x, y);
    }),
    ignoreElements()
  );

  //
  // VisualElementController load
  //
  return merge(
    birdActions$,
    clickAction$,
    loadState$,
    loadVisualElementController(
      visualController,
      visualElementController,
      unload$,
      myActions$,
      state$,
      deps,
      {
        create: () =>
          ImageState.actions.add(visualId, {
            mode,
            isExternal,
            url,
            alt: model.getAlternateText(),
            loadState: 'loading',
          }),
        delete: () => ImageState.actions.delete(visualId),
      }
    ),
    unload$.pipe(
      tap(() => {
        if (currentTipEvent) {
          currentTipEvent.release();
        }
        tipHandle.detach();
        model.release();
      }),
      ignoreElements()
    )
  );
};
